import { css } from "@emotion/css";

import { convertActionBarTextToTestIDFormat } from "./utils";
import { Text } from "../../foundations/typography/Text";
import { useColors } from "../../hooks/useColors";
import { BasicButton } from "../button/BasicButton";
import { ButtonGroup } from "../button/ButtonGroup";
import { GhostButton } from "../button/GhostButton";
import { PrimaryButton } from "../button/PrimaryButton";

export interface ActionBarProps {
  primaryText: string;
  secondaryText?: string;
  onPrimaryClick: () => void;
  onSecondaryClick?: () => void;
  onBackClick?: () => void;
  backText?: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  container?: boolean;
  testID?: string;
  saveButtonOverride?: JSX.Element;
}

export function ActionBar(props: ActionBarProps) {
  const {
    primaryText,
    secondaryText,
    backText,
    onPrimaryClick,
    onSecondaryClick,
    onBackClick,
    isDisabled,
    isLoading,
    container,
    saveButtonOverride,
    testID = "action-bar",
  } = props;
  const colors = useColors();

  const convertedTestIDs = convertActionBarTextToTestIDFormat(
    primaryText,
    secondaryText,
    backText
  );

  return (
    <div
      className={css(
        styles.wrapper,
        { borderTop: `1px solid ${colors["border-base"]}` },
        container && styles.withPadding,
        css({
          justifyContent: onBackClick ? "space-between" : "right",
        })
      )}
      data-testid={testID}
    >
      {onBackClick && (
        <GhostButton
          testID={`section-${convertedTestIDs.convertedBackText}-button`}
          onPress={onBackClick}
          size="medium"
          leftIcon="chevron_left"
          isDisabled={isDisabled || isLoading}
        >
          <Text color="text-weak">{backText}</Text>
        </GhostButton>
      )}
      <ButtonGroup>
        {onSecondaryClick && (
          <BasicButton
            testID={`section-${convertedTestIDs.convertedSecondaryText}-button`}
            onPress={onSecondaryClick}
            size="medium"
            isDisabled={isDisabled || isLoading}
          >
            {secondaryText}
          </BasicButton>
        )}
        {saveButtonOverride || (
          <PrimaryButton
            testID={`section-${convertedTestIDs.convertedPrimaryText}-button`}
            onPress={onPrimaryClick}
            size="medium"
            isDisabled={isDisabled || isLoading}
            isLoading={isLoading}
          >
            {primaryText}
          </PrimaryButton>
        )}
      </ButtonGroup>
    </div>
  );
}

const styles = {
  withPadding: css({
    paddingLeft: "var(--space-5)",
    paddingRight: "var(--space-5)",
  }),
  wrapper: css({
    display: "flex",
    justifyContent: "end",
    width: "100%",
    paddingTop: "var(--space-4)",
    paddingBottom: "var(--space-4)",
    boxSizing: "border-box",
  }),
};
